import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, FileText, Images, Paperclip, X } from "lucide-react";
import { formatDateTime } from "@/utils/formatDate";
import { flattenAttachmentPages, useRoomAttachments } from "../api/chatApi";
import type { ChatRoomAttachment } from "../types";
import { MediaLightbox } from "./MediaLightbox";

interface RoomMediaPanelProps {
  roomId: string;
  roomTitle: string;
  onClose: () => void;
  /** Skok do wiadomości, z której pochodzi plik (jak „Pokaż w czacie"). */
  onJump: (messageId: string) => void;
}

type Tab = "media" | "files";

function formatSize(bytes: number | null): string {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Nagłówek grupy: „Sierpień 2026" — miesiącami, jak w galerii WhatsAppa. */
function monthLabel(iso: string): string {
  try {
    const d = new Date(iso);
    const s = d.toLocaleDateString("pl-PL", { month: "long", year: "numeric" });
    return s.charAt(0).toUpperCase() + s.slice(1);
  } catch {
    return "";
  }
}

function groupByMonth(items: ChatRoomAttachment[]): [string, ChatRoomAttachment[]][] {
  const out: [string, ChatRoomAttachment[]][] = [];
  for (const item of items) {
    const label = monthLabel(item.created_at);
    const last = out[out.length - 1];
    if (last && last[0] === label) last[1].push(item);
    else out.push([label, [item]]);
  }
  return out;
}

/**
 * Panel „Multimedia i pliki" rozmowy — cała historia załączników w jednym
 * miejscu, bez przewijania czatu. Obrazy/wideo otwierają się w podglądzie
 * pełnoekranowym z przechodzeniem strzałkami, pliki pobierają się wprost,
 * a z każdej pozycji można skoczyć do wiadomości źródłowej.
 */
export function RoomMediaPanel({
  roomId,
  roomTitle,
  onClose,
  onJump,
}: RoomMediaPanelProps) {
  const [tab, setTab] = useState<Tab>("media");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const query = useRoomAttachments(roomId, tab, true);
  const { items, counts } = useMemo(
    () => flattenAttachmentPages(query.data?.pages),
    [query.data],
  );

  // Doładowanie kolejnej strony, gdy dno listy wjedzie w widok.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !query.hasNextPage) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !query.isFetchingNextPage) {
        query.fetchNextPage();
      }
    }, { rootMargin: "200px" });
    io.observe(node);
    return () => io.disconnect();
  }, [query.hasNextPage, query.isFetchingNextPage, query, items.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Esc zamyka panel tylko wtedy, gdy nie ma nad nim otwartego podglądu.
      if (e.key === "Escape" && lightboxIndex === null) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, lightboxIndex]);

  const mediaFiles = items.map((i) => i.file);

  return createPortal(
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/30" onClick={onClose}>
      <aside
        className="w-full max-w-md h-full bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Multimedia i pliki rozmowy"
      >
        <header className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <Images size={16} className="text-gray-500" />
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              Multimedia i pliki
            </h3>
            <p className="text-[11px] text-gray-500 truncate">{roomTitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Zamknij"
            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
          >
            <X size={16} />
          </button>
        </header>

        <div className="flex border-b border-gray-200 flex-shrink-0 text-xs">
          {(["media", "files"] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setTab(t);
                setLightboxIndex(null);
              }}
              className={
                "flex-1 px-3 py-2 font-medium border-b-2 -mb-px " +
                (tab === t
                  ? "border-red-500 text-red-600"
                  : "border-transparent text-gray-500 hover:text-gray-800")
              }
            >
              {t === "media" ? "Multimedia" : "Pliki"}{" "}
              <span className="text-gray-400">
                ({t === "media" ? counts.media : counts.files})
              </span>
            </button>
          ))}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3">
          {query.isLoading && (
            <p className="text-xs text-gray-500 px-1">Wczytuję…</p>
          )}
          {!query.isLoading && items.length === 0 && (
            <p className="text-xs text-gray-500 px-1">
              {tab === "media"
                ? "W tej rozmowie nie ma jeszcze zdjęć ani filmów."
                : "W tej rozmowie nie ma jeszcze plików."}
            </p>
          )}

          {groupByMonth(items).map(([label, group]) => (
            <section key={label} className="mb-4">
              <h4 className="text-[11px] uppercase tracking-wide text-gray-400 mb-1.5 px-1">
                {label}
              </h4>
              {tab === "media" ? (
                <div className="grid grid-cols-3 gap-1">
                  {group.map((item) => {
                    const idx = items.indexOf(item);
                    const isVideo = item.file.content_type?.startsWith("video/");
                    const isAudio = item.file.content_type?.startsWith("audio/");
                    return (
                      <button
                        key={`${item.message_id}-${item.id}`}
                        type="button"
                        onClick={() => setLightboxIndex(idx)}
                        title={`${item.file.original_filename} · ${item.sender?.full_name ?? ""} ${formatDateTime(item.created_at, "")}`}
                        className="relative aspect-square overflow-hidden rounded bg-gray-100 hover:opacity-90"
                      >
                        {isAudio ? (
                          <span className="absolute inset-0 flex items-center justify-center text-gray-500">
                            <Paperclip size={18} />
                          </span>
                        ) : (
                          <img
                            src={item.file.url ?? ""}
                            alt={item.file.original_filename}
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        )}
                        {isVideo && (
                          <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 text-[9px] text-white">
                            wideo
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <ul className="space-y-1">
                  {group.map((item) => (
                    <li
                      key={`${item.message_id}-${item.id}`}
                      className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-gray-50"
                    >
                      <FileText size={16} className="text-gray-400 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-900 truncate">
                          {item.file.original_filename}
                        </p>
                        <p className="text-[10px] text-gray-500 truncate">
                          {item.sender?.full_name ?? "—"} · {formatDateTime(item.created_at, "")}
                          {item.file.size_bytes != null &&
                            ` · ${formatSize(item.file.size_bytes)}`}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          onJump(item.message_id);
                          onClose();
                        }}
                        title="Pokaż w rozmowie"
                        className="text-[10px] text-gray-500 hover:text-gray-900 px-1 flex-shrink-0"
                      >
                        w rozmowie
                      </button>
                      <a
                        href={item.file.url ?? "#"}
                        download={item.file.original_filename}
                        target="_blank"
                        rel="noreferrer"
                        title="Pobierz"
                        className="rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-900 flex-shrink-0"
                      >
                        <Download size={14} />
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          <div ref={sentinelRef} />
          {query.isFetchingNextPage && (
            <p className="text-[11px] text-gray-400 text-center py-2">
              Wczytuję starsze…
            </p>
          )}
        </div>
      </aside>

      {lightboxIndex !== null && items[lightboxIndex] && (
        <MediaLightbox
          items={mediaFiles}
          index={lightboxIndex}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
          caption={`${items[lightboxIndex].sender?.full_name ?? ""} · ${formatDateTime(items[lightboxIndex].created_at, "")}`}
          onJumpToMessage={() => {
            onJump(items[lightboxIndex].message_id);
            onClose();
          }}
        />
      )}
    </div>,
    document.body,
  );
}
